import React, { FC, useState, useEffect, useRef, createContext } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Space, Table, Popover, Typography, Button } from 'antd'
import { IssuesCloseOutlined, FileSearchOutlined, SearchOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import type { IAppState } from '@/store/reducers'
import { getProjectInfoApplyList } from '@/services/table.service'
import { getErrorStr } from '@/store/sagas/table.saga'
import { requestFail } from '@/store/actions/common.action'
import { getWorkgroup } from '@/store/actions/tableEdit.action'
import PageBreadcrumb from '@/components/pageBreadcrumb'
import { getDicClass } from '@/pages/charts'
import ChangeApprSearchPop from '@/pages/table2Mana/components/changeApprSearchPop'
import { IEditProjectInfoApprParam } from '@/store/models/tableEdit.model'
import Nav from '@/components/nav'

export const DrawerShowContext = createContext<{
    showSearchPop: boolean
    setShowSearchPop: any
    classArr: any[]
    getList: any
}>({
    showSearchPop: false,
    setShowSearchPop: null,
    classArr: [],
    getList: null
})

const TableEditApplyList: FC = () => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const { Text } = Typography
    const history: any = useHistory()
    const route = useSelector((state: IAppState) => state.router)
    const workgroupList = useSelector((state: IAppState) => state.tableEditData.workgroupList)
    const tableEditData = useSelector((state: IAppState) => state.tableEditData.table2EditData)
    const dicClassList = useSelector((state: IAppState) => state.tableEditData.dicClass)
    const authInfo = useSelector((state: IAppState) => state.auth.authInfo)
    const userInfo = useSelector((state: IAppState) => state.auth.userInfo)
    const classification = useSelector((state: IAppState) => state.tableEditData.techClassification)

    const [loading, setLoading] = useState(true)
    const [columns, setColumns] = useState<any[]>([])
    const [dataSource, setdataSource] = useState([])
    const [classArr, setClassArr] = useState<any[]>([])
    const [showSearchPop, setShowSearchPop] = useState(false)
    const [showSearchObj, setShowSearchObj] = useState<IEditProjectInfoApprParam>({})
    const [winW, setWinW] = useState(0)
    const [winH, setWinH] = useState(0)
    const [pagination, setPagination] = useState({
        pageIndex: 1,
        pageSize: 10,
        total: 0
    })

    useEffect(() =>{
        const offsetW = 40
        const offsetH = 300
        setWinW(document.body.clientWidth - offsetW)
        setWinH(document.body.clientHeight - offsetH)
        const onResize = () => {
            setWinW(document.body.clientWidth - offsetW)
            setWinH(document.body.clientHeight - offsetH)
        }
        window.addEventListener('resize', onResize)
        return () => {
            window.removeEventListener('resize', onResize)
        }
    },[])
    const locationSearch = useRef<{
        workGroupId: number | undefined
        projectInfoId: number | undefined
    }>({
        workGroupId: undefined,
        projectInfoId: undefined,
    })

    const viewButton = (text: string, record: any) => (<Space>
        {/* 添加变更申请 */}
        {authInfo && Reflect.has(authInfo, 'auditChange') && record.auditStatus === 1 && <Text className='txt_blue hand' title={t('approvalEdit')}
            onClick={() => {
                history.push('/projectInfoChangeAppr?workGroupId=' + record.techProject.techClass.workGroup.id + '&applyId=' + record.id)
            }}
        ><IssuesCloseOutlined style={{ fontSize: 16 }} /></Text>}
        {/* 查看变更详情 */}
        {authInfo && Reflect.has(authInfo, 'viewAuditChange') && <Text className='txt_blue hand' title={t('viewAuditChange')}
            onClick={() => {
                history.push('/projectInfoChangeAppr?workGroupId=' + record.techProject.techClass.workGroup.id + '&applyId=' + record.id + '&isView=true')
            }}
        ><FileSearchOutlined style={{ fontSize: 16 }} /></Text>}
    </Space>)
    const initColumns = () => {
        setColumns([
            {
                title: () => <span>{t('Work Group')}</span>,
                dataIndex: ['techProject', 'techClass', 'workGroup', 'workGroupName'],
                key: 'workGroupName',
                width: 150,
                fixed:true,
                ellipsis: { showTitle: true }
            },
            {
                title: () => <span>{t('Classification')}</span>,
                dataIndex: ['techProject', 'techClass', 'classificationName'],
                key: 'Classification',
                width: 150,
                fixed:true,
                ellipsis: { showTitle: true }
            },
            {
                title: () => <span>{t('Tech Name')}</span>,
                dataIndex: ['techProject', 'techName'],
                key: 'techName',
                ellipsis: { showTitle: true },
                render (text: string) {
                    return (<Popover 
                        title=""
                        content={
                            <div dangerouslySetInnerHTML={{
                                __html: (text.includes('"str":') && text.includes('"obj":')) ? (JSON.parse(text).str) : `<span>${text}</span>`
                            }}></div>
                        }
                    >
                        <div dangerouslySetInnerHTML={{
                            __html: (text.includes('"str":') && text.includes('"obj":')) ? (JSON.parse(text).str) : `<span>${text}</span>`
                        }}></div>
                    </Popover>)
                }
            },
            {
                title: () => <span>{t('PTN')}</span>,
                dataIndex: ['techProject', 'techPtn'],
                key: 'techPtn',
                width: 150,
                ellipsis: { showTitle: true }
            },
            {
                title: () => <span>{t('creator')}</span>,
                dataIndex: ['creator'],
                key: 'creator',
                width: 200,
                ellipsis: { showTitle: true }
            },
            {
                title: () => <span>{t('createTime')}</span>,
                dataIndex: 'createTime',
                key: 'createTime',
                width: 180,
                ellipsis: { showTitle: true },
                // 1 待审核，2 审核通过，3 审核不通过
                render (text: Date) {
                    return moment(text).format('YYYY-MM-DD HH:mm:ss')
                }
            },
            {
                title: () => <span>{t('auditor')}</span>,
                dataIndex: ['auditor'],
                key: 'auditor',
                width: 200,
                ellipsis: { showTitle: true }
            },
            {
                title: () => <span>{t('auditTime')}</span>,
                dataIndex: 'auditTime',
                key: 'auditTime',
                width: 180,
                ellipsis: { showTitle: true },
                render (text: Date) {
                    return text ? moment(text).format('YYYY-MM-DD HH:mm:ss') : ''
                }
            },
            {
                title: () => <span>{t('auditStatus')}</span>,
                dataIndex: 'auditStatus',
                key: 'auditStatus',
                width: 150,
                ellipsis: { showTitle: true },
                // 1 待审核，2 审核通过，3 审核不通过
                render (text: 1 | 2 | 3) {
                    return text === 1 ? t('auditWait') : (text === 2 ? t('auditPass') : t('auditUnPass'))
                }
            },
            {
                title: () => <span>{t('auditComments')}</span>,
                dataIndex: ['auditComments'],
                key: 'auditComments',
                className: 'lang_column',
                width: 250,
                ellipsis: { showTitle: true },
                render (value:any) {
                    if (value && (value.includes('<div') || value.includes('<span'))) {
                        return <div
                            dangerouslySetInnerHTML={{
                                __html: (value.includes('"str":') && value.includes('"obj":')) ? JSON.parse(value).str : value
                            }}
                        ></div>
                    } else {
                        return <div style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}
                            title={value}
                        >
                            {wrapText(value)}
                        </div>
                    }
                }
            },
            {
                title: () => <span>{t('Action')}</span>,
                dataIndex: 'operation',
                key: 'operation',
                width: 100,
                fixed: 'right',
                render: viewButton
            }
        ])
    }
    const wrapText = (text: string) => {
        const br = <br/>
        const empty = <span>&nbsp;&nbsp;&nbsp;&nbsp;</span>
        let result
        const contentStr = text ? text.split(';') : ''
        if(contentStr.length < 2) {
            return text
        }
        for(let j = 0; j < contentStr.length; j++) {
            if (j == 0) {
                result = contentStr[j]
            } else if (contentStr[j].startsWith(' ')) {
                result = <span>{result}{br}{empty}{contentStr[j]}</span>
            } else {
                result = <span>{result}{br}{contentStr[j]}</span>
            }
        }
        return <div>{result}</div>
    }
    const getList = async (paginationProp: {
        pageIndex: number
        pageSize: number
        total: number
    }, {
        creator,
        auditor,
        workGroupId,
        techClassId,
        techName,
        techPtn,
        auditStatus,
    }: IEditProjectInfoApprParam) => {
        console.log(paginationProp, {
            creator,
            auditor,
            workGroupId,
            techClassId,
            techName,
            techPtn,
            auditStatus,
        })
        if (!paginationProp) {
            paginationProp = { ...pagination }
        }
        try {
            setLoading(true)
            setShowSearchObj({
                pageNo: paginationProp.pageIndex,
                pageSize: paginationProp.pageSize,
                creator,
                auditor,
                workGroupId,
                techClassId,
                techName,
                techPtn,
                auditStatus
            })
            const res = await getProjectInfoApplyList({
                pageNo: paginationProp.pageIndex,
                pageSize: paginationProp.pageSize,
                creator,
                auditor,
                workGroupId,
                techClassId,
                techName,
                techPtn,
                auditStatus
            })
            console.log('res: ', res)
            setdataSource(res.content)
            setPagination({
                ...paginationProp,
                total: res.totalElements
            })
        } catch (error: any) {
            const str = getErrorStr(error)
            dispatch(requestFail(str))
        }
        setLoading(false)
    }
    
    // pageSize 变更后，会重复触发 pageChange 事件
    const pageSizeChanged = useRef<boolean>(false)
    const onPageChange = (e: number) => {
        if (pageSizeChanged.current) return
        onPageSizeChange(e,pagination.pageSize)
    }

    const onPageSizeChange = (current: number, size: number) => {
        pageSizeChanged.current = true
        if (authInfo) {
            if (Reflect.has(authInfo, 'auditChange')) {
                getList({
                    ...pagination,
                    pageIndex: current,
                    pageSize:size
                }, {
                    ...showSearchObj
                })
            } else {
                getList({
                    ...pagination,
                    pageIndex: current,
                    pageSize:size
                }, {
                    ...showSearchObj,
                    creator: userInfo?.login
                })
            }
        }
        setTimeout(() => {
            pageSizeChanged.current = false
        }, 100)
    }

    const [routes, setRoutes] = useState([
        {
            path: '/indexPage',
            breadcrumbName: t('indexPage'),
        },
        // {
        //     path: '/projectInfo',
        //     breadcrumbName: t('editApply'),
        // },
        {
            path: '/projectInfoEdit',
            breadcrumbName: t('approvalList'),
        },
    ])

    useEffect(() => {
        const routeParams = new URLSearchParams(location.search)
        const worGroupIdParam = routeParams.get('workGroupId')
        const projectInfoId = routeParams.get('projectId')
        locationSearch.current = {
            workGroupId: +(worGroupIdParam || ''),
            projectInfoId: +(projectInfoId || ''),
        }

        // 获取字典列表，作为下拉选项
        if (Object.keys(dicClassList).length === 0) {
            // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
            getDicClass(dispatch, (a: boolean): void => {})
        }

        if (!workgroupList.length) {
            // 获取 workgroupList
            dispatch(getWorkgroup())
        }
        initColumns()
        const offsetH = 300
        setWinH(document.body.clientHeight - offsetH)
        const onResize = () => {
            setWinH(document.body.clientHeight - offsetH)
        }
        window.addEventListener('resize', onResize)
        return () => {
            window.removeEventListener('resize', onResize)
        }
    }, [])
    useEffect(() => {
        if (authInfo) {
            if (Reflect.has(authInfo, 'auditChange')) {
                getList(pagination, {
                    ...showSearchObj
                })
            } else {
                getList(pagination, {
                    ...showSearchObj,
                    creator: userInfo?.login
                })
            }
        }
    }, [authInfo])

    useEffect(() => {
        if (locationSearch.current.workGroupId) {
            // const findObj = workgroupList.find(item => item.id === locationSearch.current.workGroupId)
            const _routes = [...routes]
            // if (findObj) {
            //     _routes[_routes.length - 2].breadcrumbName = findObj.groupName
            // }
            setRoutes(_routes)
        }
    }, [workgroupList, tableEditData])
    useEffect(() => {
        const _classification = [...classification]
        const groupByCategory = _classification.reduce((group: any, product) => {
            const { classId } = product
            group[classId] = group[classId] || []
            group[classId].push(product)
            return group
        }, {})
        const _classArr: any[] = []
        Object.keys(groupByCategory).forEach(key => {
            _classArr.push(groupByCategory[key][0])
        })
        setClassArr([..._classArr])
    }, [classification])
    return (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <Nav dispatch={dispatch} />
            <PageBreadcrumb routerParam={routes}
                history={history}
                route={route}
                dispatch={dispatch}
            />
            <Space direction="vertical" size={20} style={{ margin: '0 10px' }}>
                <Button type='primary'
                    icon={<SearchOutlined />}
                    onClick={() => {
                        setShowSearchPop(!showSearchPop)
                    }}
                >{t('search')}</Button>
                <div  style={{ width: winW }}>
                    <Table columns={columns} dataSource={dataSource} rowKey='id'
                        loading={loading}
                        scroll={{ x: 'max-content', y: winH }}
                        pagination={{
                            current: pagination.pageIndex,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            onShowSizeChange: onPageSizeChange,
                            onChange: onPageChange
                        }}
                    />
                </div>
            </Space>
            <DrawerShowContext.Provider value={{ showSearchPop, setShowSearchPop, classArr, getList }}>
                <ChangeApprSearchPop />
            </DrawerShowContext.Provider>
        </Space>
    )
}

export default TableEditApplyList
